import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';
import type { Coordinates } from '../types';
import type { RouteResult, RouteOption, TravelMode } from '../lib/routing';
import type { WhereAmI } from '../lib/geolocation';
import { EMPTY_TRACK, type TrackStats } from '../lib/traffic';

export type NavPanel = 'none' | 'directions' | 'location';

export interface RouteEndpoint {
  coords: Coordinates | null;
  label: string;
}

interface NavStore {
  // ---- panel visibility ----
  panel: NavPanel;
  setPanel: (panel: NavPanel) => void;
  openDirections: () => void;
  openLocation: () => void;
  closePanel: () => void;

  // ---- "Where am I?" ----
  whereAmI: WhereAmI | null;
  locating: boolean;
  locationError: string | null;
  followDevice: boolean;
  setWhereAmI: (
    v: WhereAmI | null | ((prev: WhereAmI | null) => WhereAmI | null)
  ) => void;
  setLocating: (v: boolean) => void;
  setLocationError: (v: string | null) => void;
  setFollowDevice: (v: boolean) => void;
  setAddress: (address: string | null, resolving: boolean) => void;

  // ---- live tracking ----
  tracking: boolean;
  track: TrackStats;
  /** breadcrumb of where the user has been while tracking */
  trail: [number, number][];
  setTracking: (v: boolean) => void;
  setTrack: (t: TrackStats | ((prev: TrackStats) => TrackStats)) => void;
  pushTrail: (lngLat: [number, number]) => void;
  resetTrack: () => void;

  // ---- routing ----
  mode: TravelMode;
  setMode: (mode: TravelMode) => void;

  origin: RouteEndpoint;
  destination: RouteEndpoint;
  setOrigin: (e: RouteEndpoint) => void;
  setDestination: (e: RouteEndpoint) => void;
  swapEndpoints: () => void;
  useMyLocationAsOrigin: () => void;

  route: RouteResult | null;
  activeRouteIndex: number;
  routing: boolean;
  routeError: string | null;
  showRoutePanel: boolean;
  showSteps: boolean;
  setRoute: (r: RouteResult | null) => void;
  setActiveRouteIndex: (i: number) => void;
  setRouting: (v: boolean) => void;
  setRouteError: (v: string | null) => void;
  setShowRoutePanel: (v: boolean) => void;
  setShowSteps: (v: boolean) => void;
  clearRoute: () => void;

  // selectors
  activeRoute: () => RouteOption | null;
}

const emptyEndpoint: RouteEndpoint = { coords: null, label: '' };

export const useNavStore = create<NavStore>()(
  devtools(
    persist(
      (set, get) => ({
        panel: 'none',
        setPanel: (panel) => set({ panel }),
        openDirections: () => set({ panel: 'directions' }),
        openLocation: () => set({ panel: 'location' }),
        closePanel: () => set({ panel: 'none' }),

        whereAmI: null,
        locating: false,
        locationError: null,
        followDevice: false,
        setWhereAmI: (v) =>
          set((prev) => ({
            whereAmI: typeof v === 'function' ? v(prev.whereAmI) : v,
          })),
        setLocating: (locating) => set({ locating }),
        setLocationError: (locationError) => set({ locationError }),
        setFollowDevice: (followDevice) => set({ followDevice }),
        setAddress: (address, resolvingAddress) =>
          set((prev) =>
            prev.whereAmI
              ? { whereAmI: { ...prev.whereAmI, address, resolvingAddress } }
              : {}
          ),

        tracking: false,
        track: EMPTY_TRACK,
        trail: [],
        setTracking: (tracking) => set({ tracking }),
        setTrack: (t) =>
          set((prev) => ({ track: typeof t === 'function' ? t(prev.track) : t })),
        pushTrail: (lngLat) =>
          set((prev) => {
            const last = prev.trail[prev.trail.length - 1];
            // skip near-duplicate points so the trail stays light
            if (last && Math.abs(last[0] - lngLat[0]) < 1e-5 && Math.abs(last[1] - lngLat[1]) < 1e-5) {
              return {};
            }
            return { trail: [...prev.trail, lngLat].slice(-2000) };
          }),
        resetTrack: () => set({ track: EMPTY_TRACK, trail: [] }),

        mode: 'car',
        setMode: (mode) => set({ mode }),

        origin: emptyEndpoint,
        destination: emptyEndpoint,
        setOrigin: (origin) => set({ origin }),
        setDestination: (destination) => set({ destination }),
        swapEndpoints: () =>
          set((prev) => ({ origin: prev.destination, destination: prev.origin })),
        useMyLocationAsOrigin: () => {
          const { whereAmI } = get();
          if (!whereAmI) return;
          set({
            origin: {
              coords: whereAmI.coords,
              label: whereAmI.address || 'My location',
            },
          });
        },

        route: null,
        activeRouteIndex: 0,
        routing: false,
        routeError: null,
        showRoutePanel: false,
        showSteps: false,
        setRoute: (route) => set({ route, activeRouteIndex: 0 }),
        setActiveRouteIndex: (activeRouteIndex) => set({ activeRouteIndex }),
        setRouting: (routing) => set({ routing }),
        setRouteError: (routeError) => set({ routeError }),
        setShowRoutePanel: (showRoutePanel) => set({ showRoutePanel }),
        setShowSteps: (showSteps) => set({ showSteps }),
        clearRoute: () =>
          set({ route: null, activeRouteIndex: 0, routeError: null, showRoutePanel: false }),

        activeRoute: () => {
          const { route, activeRouteIndex } = get();
          if (!route || !route.routes.length) return null;
          return route.routes[Math.min(activeRouteIndex, route.routes.length - 1)];
        },
      }),
      {
        name: 'leadmap3d-nav-store',
        partialize: (state) => ({
          mode: state.mode,
          origin: state.origin,
          destination: state.destination,
          showSteps: state.showSteps,
        }),
      }
    )
  )
);